import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  DEFAULT_LOCALE,
  getLocaleOptions,
  isSupportedLocale,
  readStoredLocale,
  translateLocaleKey,
  writeStoredLocale,
} from "domain/locale";

const LocaleContext = createContext(null);

export const useLocale = () => {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }

  return context;
};

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => readStoredLocale());
  const localeOptions = useMemo(() => getLocaleOptions(), []);

  const setLocale = useCallback((newLocale) => {
    if (isSupportedLocale(newLocale)) {
      setLocaleState(newLocale);
      writeStoredLocale(newLocale);
    }
  }, []);

  const t = useCallback(
    (key, fallback = "") =>
      translateLocaleKey({
        key,
        locale,
        fallback,
        fallbackLocale: DEFAULT_LOCALE,
      }),
    [locale],
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      locales: localeOptions,
    }),
    [locale, localeOptions, setLocale, t],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export default LocaleContext;
